import React from 'react';

import { CompoundOrgInput, type OrgField, type OrgFieldName } from '../CompoundInput';
import Checkbox from '../Input2/Checkbox';
import Input from '../Input2/Input';
import { Scrollable } from '../Scrollable';

export type OrgFlags = {
  isRsuGranting: boolean;
  isEquityGranting: boolean;
  /** ISO-3166-2 subdivision code (e.g. 'CA', 'NY'). Null when unknown. */
  hqState: string | null;
};

type BoolFlagKey = 'isRsuGranting' | 'isEquityGranting';

type OrgDataSectionProps = {
  orgFields: Partial<Record<OrgFieldName, OrgField[]>> | undefined;
  onAddValue: (name: OrgFieldName, value: string, source: string) => void;
  onDeleteValue: (name: OrgFieldName, source: string) => void;
  onMakePrimary: (name: OrgFieldName, source: string) => void;
  onRemovePrimary: (name: OrgFieldName, source: string) => void;
  flags?: OrgFlags;
  onSetFlag?: (flag: BoolFlagKey, value: boolean) => void;
  onSetHqState?: (value: string | null) => void;
  isFlagsPending?: boolean;
};

const FIELDS: { name: OrgFieldName; placeholder: string; value?: string; disabled?: boolean }[] = [
  { name: 'name', placeholder: 'Name' },
];

const FLAG_ROWS: { key: BoolFlagKey; label: string }[] = [
  { key: 'isRsuGranting', label: 'RSU granting' },
  { key: 'isEquityGranting', label: 'Equity granting' },
];

export function OrgDataSection({
  orgFields,
  onAddValue,
  onDeleteValue,
  onMakePrimary,
  onRemovePrimary,
  flags,
  onSetFlag,
  onSetHqState,
  isFlagsPending,
}: OrgDataSectionProps) {
  const [expandedField, setExpandedField] = React.useState<OrgFieldName | undefined>();
  const [hqStateDraft, setHqStateDraft] = React.useState<string>(flags?.hqState ?? '');
  // Reset the draft when the server value changes, via the "adjust state during
  // render" pattern (see SidebarNotes) instead of a mirroring effect — the
  // effect double-rendered and could overwrite an unsaved edit.
  const prevHqState = React.useRef(flags?.hqState);
  if (prevHqState.current !== flags?.hqState) {
    prevHqState.current = flags?.hqState;
    setHqStateDraft(flags?.hqState ?? '');
  }

  return (
    <Scrollable>
      {FIELDS.map((input) => (
        <CompoundOrgInput
          fields={orgFields?.[input.name]}
          name={input.name}
          placeholder={input.placeholder}
          title={input.placeholder}
          key={input.name}
          expanded={expandedField === input.name}
          setExpanded={(n, v) => setExpandedField(v ? n : undefined)}
          onDeleteValue={onDeleteValue}
          onAdd={onAddValue}
          onRemovePrimary={onRemovePrimary}
          onMakePrimary={onMakePrimary}
          disabled={input.disabled}
          value={input.value}
        />
      ))}

      {flags && onSetFlag && (
        <div className="border-t border-gray-100 px-4 py-3 dark:border-gray-800">
          <div className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">Org flags</div>
          <Checkbox
            size="sm"
            disabled={isFlagsPending}
            options={FLAG_ROWS.map((row) => ({ value: row.key, label: row.label }))}
            value={FLAG_ROWS.filter((row) => flags[row.key]).map((row) => row.key)}
            onChange={(next) => {
              for (const row of FLAG_ROWS) {
                const wasChecked = flags[row.key];
                const isChecked = next.includes(row.key);
                if (wasChecked !== isChecked) onSetFlag(row.key, isChecked);
              }
            }}
          />
          {onSetHqState && (
            <div className="mt-3">
              <div className="mb-1 text-xs font-medium text-gray-600 dark:text-gray-400">HQ state</div>
              <Input
                size="sm"
                placeholder="2-letter (e.g. CA)"
                value={hqStateDraft}
                onChange={(e) => setHqStateDraft(e.target.value.toUpperCase().slice(0, 2))}
                onBlur={() => {
                  const normalized = hqStateDraft.trim().toUpperCase();
                  const next = normalized.length === 2 ? normalized : null;
                  if (next !== (flags.hqState ?? null)) onSetHqState(next);
                }}
                disabled={isFlagsPending}
              />
            </div>
          )}
        </div>
      )}
    </Scrollable>
  );
}
